import React, { useEffect, useMemo, useRef } from "react";

import { Box } from "@chakra-ui/react";
import { useBusinessCompliance } from "contexts/business-compliance-context";
import {
  DEFAULT_BUSINESS_DETAILS,
  useBusinesses,
} from "contexts/businesses-context";
import { BvnNinInformation } from "features/business-compliance/selected-business/BvnNinInformation";
import { ComplianceTrail } from "features/business-compliance/selected-business/ComplianceTrail";
import { FaceVerification } from "features/business-compliance/selected-business/FaceVerification";
import { useElementWidth } from "hooks/useElementWidth";
import { useParams } from "react-router-dom";
import { REGISTERED_BUSINESS_COMPANY_TYPE } from "utils/constants";

import { Accordion } from "ui/accordion";
import { IAccordionItem } from "ui/accordion/types";
import { Spinner } from "ui/spinner";

import { businessDetailsTabCardCss } from "../styles";
import { BusinessDetailsDirectorsInformation } from "./BusinessDetailsDirectorsInformation";
import { BusinessDetailsDocuments } from "./BusinessDetailsDocuments";
import { BusinessDetailsHeader } from "./BusinessDetailsHeader";
import { BusinessDetailsInformation } from "./BusinessDetailsInformation";
import { BusinessDetailsKYBVerificationResults } from "./BusinessDetailsKYBVerificationResults";

const LG_WIDTH = 745;
const MD_WIDTH = 600;
const SM_WIDTH = 400;

export const BusinessDetails = () => {
  const { businessId } = useParams();
  const { handleGetComplianceTrails, isLoadingComplianceTrails } =
    useBusinessCompliance();
  const { businessDetails, isLoadingBusinessDetails } = useBusinesses();
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const isContentWrapperBelow745 = useElementWidth(contentWrapperRef, LG_WIDTH);
  const isContentWrapperBelow600 = useElementWidth(contentWrapperRef, MD_WIDTH);
  const isContentWrapperBelow450 = useElementWidth(contentWrapperRef, SM_WIDTH);

  useEffect(() => {
    if (!businessId) {
      return;
    }
    void handleGetComplianceTrails(businessId);
  }, [businessId]);

  const accordionItems: IAccordionItem[] = useMemo(
    () => [
      {
        value: "businessInfo",
        header: (
          <BusinessDetailsHeader
            title="Business Details"
            isLoadingBusinessDetails={isLoadingBusinessDetails}
            businessDetails={businessDetails}
          />
        ),
        body: (
          <>
            {businessDetails ? (
              <BusinessDetailsInformation
                business={businessDetails || DEFAULT_BUSINESS_DETAILS}
                isContentWrapperBelow745={isContentWrapperBelow745}
                isContentWrapperBelow600={isContentWrapperBelow600}
              />
            ) : (
              <Spinner size="30px" />
            )}
          </>
        ),
      },
      ...(businessDetails?.business?.companyData?.companyType !==
      REGISTERED_BUSINESS_COMPANY_TYPE
        ? [
            {
              value: "bvnNinInformation",
              header: (
                <BusinessDetailsHeader
                  title="BVN and NIN Information"
                  isLoadingBusinessDetails={isLoadingBusinessDetails}
                  businessDetails={businessDetails}
                />
              ),
              body: (
                <BvnNinInformation
                  business={businessDetails || DEFAULT_BUSINESS_DETAILS}
                  isContentWrapperBelow745={isContentWrapperBelow745}
                  isContentWrapperBelow600={isContentWrapperBelow600}
                />
              ),
            },
            {
              value: "faceVerification",
              header: (
                <BusinessDetailsHeader
                  title="Face Verification"
                  isLoadingBusinessDetails={isLoadingBusinessDetails}
                  businessDetails={businessDetails}
                />
              ),
              body: (
                <FaceVerification
                  business={businessDetails || DEFAULT_BUSINESS_DETAILS}
                  isContentWrapperBelow745={isContentWrapperBelow745}
                  isContentWrapperBelow600={isContentWrapperBelow600}
                />
              ),
            },
            {
              value: "businessAddress",
              header: (
                <BusinessDetailsHeader
                  title="Business Address"
                  isLoadingBusinessDetails={isLoadingBusinessDetails}
                  businessDetails={businessDetails}
                />
              ),
              body: (
                <BusinessDetailsDocuments
                  business={businessDetails || DEFAULT_BUSINESS_DETAILS}
                  isContentWrapperBelow745={isContentWrapperBelow745}
                  isContentWrapperBelow600={isContentWrapperBelow600}
                />
              ),
            },
          ]
        : [
            {
              value: "businessDocs",
              header: (
                <BusinessDetailsHeader
                  title="Business Documents"
                  isLoadingBusinessDetails={isLoadingBusinessDetails}
                  businessDetails={businessDetails}
                />
              ),
              body: (
                <>
                  {businessDetails ? (
                    <BusinessDetailsDocuments
                      business={businessDetails}
                      isContentWrapperBelow745={isContentWrapperBelow745}
                      isContentWrapperBelow600={isContentWrapperBelow600}
                    />
                  ) : (
                    <Spinner size="30px" />
                  )}
                </>
              ),
            },
            {
              value: "directorsInfo",
              header: (
                <BusinessDetailsHeader
                  title="Director's Information"
                  isLoadingBusinessDetails={isLoadingBusinessDetails}
                  businessDetails={businessDetails}
                />
              ),
              body: (
                <>
                  {businessDetails ? (
                    <BusinessDetailsDirectorsInformation
                      business={businessDetails}
                      isContentWrapperBelow745={isContentWrapperBelow745}
                      isContentWrapperBelow600={isContentWrapperBelow600}
                    />
                  ) : (
                    <Spinner size="30px" />
                  )}
                </>
              ),
            },
            {
              value: "kybVerificatioResults",
              header: (
                <BusinessDetailsHeader
                  title="KYB Verification Results"
                  isLoadingBusinessDetails={isLoadingBusinessDetails}
                  businessDetails={businessDetails}
                />
              ),
              body: (
                <BusinessDetailsKYBVerificationResults
                  isContentWrapperBelow745={isContentWrapperBelow745}
                  isContentWrapperBelow600={isContentWrapperBelow600}
                  data={
                    businessDetails?.business?.companyData
                      ?.kybVerificationResult || null
                  }
                />
              ),
            },
          ]),
      {
        value: "complianceTrail",
        header: (
          <BusinessDetailsHeader
            title="Compliance Trail"
            isLoadingBusinessDetails={isLoadingBusinessDetails}
            businessDetails={businessDetails}
          />
        ),
        body: isLoadingComplianceTrails ? (
          <Spinner size="30px" />
        ) : (
          <ComplianceTrail />
        ),
      },
    ],
    [
      isContentWrapperBelow745,
      isContentWrapperBelow600,
      isContentWrapperBelow450,
      businessDetails,
      isLoadingBusinessDetails,
      isLoadingComplianceTrails,
    ]
  );

  return (
    <Box
      className={`py-6 rounded-[20px] bg-white mt-1 ${isContentWrapperBelow600 || isContentWrapperBelow450 ? "h-[calc(100%-38%-16px)]" : "h-[calc(100%-133px)]"}`}
      css={businessDetailsTabCardCss}
      ref={contentWrapperRef}
    >
      <Accordion
        accordionItems={accordionItems}
        accordionRootCss={{
          overflow: "hidden scroll",
          height: "100%",
        }}
      />
    </Box>
  );
};
